/**
 * RecodeTriggerExtension — surfaces the Recode Wizard on demand, mirroring
 * IfTriggerExtension.
 *
 * Behaviour:
 *  - When the cursor is in a paragraph matching /^\s*RECODE\b/ (case-
 *    insensitive), a "Continue in Recode Wizard" chip is rendered at the end of
 *    that line. Clicking it dispatches `dataq:open-recode-wizard`.
 *  - The dispatch payload covers the whole RECODE statement:
 *      • single-line form `RECODE <assignment>` → just that paragraph;
 *      • block form `RECODE` alone → the RECODE line plus the following
 *        assignment paragraphs (lines with a top-level `=`) until a blank line,
 *        comment, or the next structural keyword.
 *  - We deliberately do NOT scan backward to absorb a surrounding `IF`
 *    (RECODE-in-IF is out of scope to generate; never overwrite the IF).
 */
import { Extension } from "@tiptap/core"
import { Plugin, PluginKey } from "@tiptap/pm/state"
import { Decoration, DecorationSet } from "@tiptap/pm/view"
import type { EditorView } from "@tiptap/pm/view"
import type { Node as ProseMirrorNode } from "@tiptap/pm/model"
import { findTopLevelEquals } from "utils/recodeWizard/recodeParser"

export interface RecodeTriggerEventDetail {
  /** "create" — bare RECODE with no assignment; "edit" — existing assignment(s). */
  mode: "create" | "edit"
  /** Full text of the captured block (RECODE line + any assignment lines). */
  lineText: string
  /** ProseMirror paragraph-node bounds for replacement. */
  from: number
  to: number
  /** 1-based paragraph index of the RECODE line — informational only. */
  paragraphIndex: number
}

const RECODE_LINE_RE = /^\s*RECODE\b\s*(.*)$/i

// Lines that end a RECODE block (the next structural element).
const STOP_KEYWORD_RE =
  /^(RECODE|Q|HIDDEN|DATA|LIST|RANGE\s+LIST|SECTION|SELECT|PER|FILL|FILLIN|IF|FOR\s+EACH|BEGIN|END|TERMINATE|SKIP\s+TO|NOTE|TEXT|EMBED|CALCULATE|VALIDATE|HIDE|EXTEND|TRIM|INSERT|RANDOMIZE|RANDOMISE|VARIANT|EXCEPTIONS|LOCATION|REDEFINE|ADJUST)\b/i

function dispatchOpen(detail: RecodeTriggerEventDetail) {
  window.dispatchEvent(
    new CustomEvent<RecodeTriggerEventDetail>("dataq:open-recode-wizard", {
      detail,
    }),
  )
}

interface ParagraphHit {
  text: string
  from: number
  to: number
  paragraphFrom: number
  paragraphTo: number
  index: number
}

function listParagraphs(doc: ProseMirrorNode): ParagraphHit[] {
  const out: ParagraphHit[] = []
  let idx = 0
  doc.descendants((node, nodePos) => {
    if (node.type.name !== "paragraph") return true
    idx += 1
    out.push({
      text: node.textContent,
      from: nodePos + 1,
      to: nodePos + node.nodeSize - 1,
      paragraphFrom: nodePos,
      paragraphTo: nodePos + node.nodeSize,
      index: idx,
    })
    return false
  })
  return out
}

function findParagraphIndexAt(paragraphs: ParagraphHit[], pos: number): number {
  for (let i = 0; i < paragraphs.length; i++) {
    const p = paragraphs[i]
    if (pos >= p.paragraphFrom && pos <= p.paragraphTo) return i
  }
  return -1
}

/**
 * From the `RECODE` line at `start`, capture following assignment paragraphs
 * (lines with a top-level `=`) until a blank line, comment, or structural
 * keyword. Returns the index of the last paragraph to include.
 */
function scanRecodeBlock(paragraphs: ParagraphHit[], start: number): number {
  let last = start
  for (let i = start + 1; i < paragraphs.length; i++) {
    const t = paragraphs[i].text.trim()
    if (!t) break
    if (t.startsWith("#")) break
    if (STOP_KEYWORD_RE.test(t)) break
    if (findTopLevelEquals(t) < 0) break
    last = i
  }
  return last
}

function makeChipClickHandler(getView: () => EditorView | null) {
  return (ev: Event) => {
    ev.preventDefault()
    ev.stopPropagation()
    const view = getView()
    if (!view) return
    const paragraphs = listParagraphs(view.state.doc)
    const idx = findParagraphIndexAt(paragraphs, view.state.selection.from)
    if (idx < 0) return
    const recodePara = paragraphs[idx]
    const m = recodePara.text.match(RECODE_LINE_RE)
    if (!m) return

    const inlineRemainder = m[1].trim()
    const hasInline =
      inlineRemainder !== "" && findTopLevelEquals(inlineRemainder) >= 0

    const lastIdx = hasInline ? idx : scanRecodeBlock(paragraphs, idx)
    const captured = paragraphs.slice(idx, lastIdx + 1)
    const text = captured.map((p) => p.text).join("\n")
    const mode: "create" | "edit" = hasInline || lastIdx > idx ? "edit" : "create"

    dispatchOpen({
      mode,
      lineText: text,
      from: recodePara.paragraphFrom,
      to: paragraphs[lastIdx].paragraphTo,
      paragraphIndex: recodePara.index,
    })
  }
}

function renderChip(getView: () => EditorView | null): HTMLElement {
  const span = document.createElement("span")
  span.className = "dataq-recode-trigger-chip"
  span.setAttribute("contenteditable", "false")
  span.style.cssText = [
    "display: inline-flex",
    "align-items: center",
    "gap: 4px",
    "margin-left: 8px",
    "padding: 0 10px",
    "height: 18px",
    "line-height: 1",
    "border-radius: 9999px",
    "background-color: var(--dq-brand)",
    "color: white",
    "font-size: 11px",
    "font-weight: 500",
    "cursor: pointer",
    "user-select: none",
    "vertical-align: middle",
    "transform: translateY(-3px)",
    "box-shadow: 0 1px 2px rgba(0,0,0,0.1)",
    "white-space: nowrap",
  ].join("; ")
  span.title = "Open Recode Wizard"
  const icon = document.createElementNS("http://www.w3.org/2000/svg", "svg")
  icon.setAttribute("width", "12")
  icon.setAttribute("height", "12")
  icon.setAttribute("viewBox", "0 0 24 24")
  icon.setAttribute("fill", "none")
  icon.setAttribute("stroke", "currentColor")
  icon.setAttribute("stroke-width", "2")
  icon.setAttribute("stroke-linecap", "round")
  icon.setAttribute("stroke-linejoin", "round")
  // Shuffle/transform glyph.
  icon.innerHTML =
    '<path d="M16 3h5v5"/><path d="M4 20 21 3"/><path d="M21 16v5h-5"/><path d="m15 15 6 6"/><path d="M4 4l5 5"/>'
  span.appendChild(icon)
  const label = document.createElement("span")
  label.textContent = "Continue in Recode Wizard"
  span.appendChild(label)
  span.addEventListener("mousedown", makeChipClickHandler(getView))
  return span
}

const triggerKey = new PluginKey<DecorationSet>("dataq-recode-trigger")

function buildDecorations(
  doc: ProseMirrorNode,
  selectionFrom: number,
  getView: () => EditorView | null,
): DecorationSet {
  const paragraphs = listParagraphs(doc)
  const idx = findParagraphIndexAt(paragraphs, selectionFrom)
  if (idx < 0) return DecorationSet.empty
  const para = paragraphs[idx]
  if (!RECODE_LINE_RE.test(para.text)) return DecorationSet.empty
  const widget = Decoration.widget(para.to, () => renderChip(getView), {
    side: 1,
    ignoreSelection: true,
    key: "recode-trigger-chip",
  })
  return DecorationSet.create(doc, [widget])
}

export const RecodeTriggerExtension = Extension.create({
  name: "recodeTrigger",

  addProseMirrorPlugins() {
    let viewRef: EditorView | null = null
    const getView = () => viewRef
    return [
      new Plugin<DecorationSet>({
        key: triggerKey,
        view: (editorView) => {
          viewRef = editorView
          return {
            destroy() {
              viewRef = null
            },
          }
        },
        state: {
          init: (_, instance) =>
            buildDecorations(instance.doc, instance.selection.from, getView),
          apply: (tr, old) => {
            if (!tr.docChanged && !tr.selectionSet) {
              return old.map(tr.mapping, tr.doc)
            }
            return buildDecorations(tr.doc, tr.selection.from, getView)
          },
        },
        props: {
          decorations(state) {
            return triggerKey.getState(state) ?? DecorationSet.empty
          },
        },
      }),
    ]
  },
})
